import React,{useEffect,useMemo,useState}from"react";
import{Box,Paper,Typography,Button,TextField,Table,TableBody,TableCell,TableContainer,TableHead,TableRow,Stack}from"@mui/material";
import LockOpenRoundedIcon from"@mui/icons-material/LockOpenRounded";
import LockRoundedIcon from"@mui/icons-material/LockRounded";
import ReceiptLongRoundedIcon from"@mui/icons-material/ReceiptLongRounded";
import PointOfSaleRoundedIcon from"@mui/icons-material/PointOfSaleRounded";
import AccountBalanceWalletRoundedIcon from"@mui/icons-material/AccountBalanceWalletRounded";
import CreditCardRoundedIcon from"@mui/icons-material/CreditCardRounded";
import{useNavigate}from"react-router-dom";
import Sidebar from"./AdminPanel/Sidebar";
import TopBar from"./AdminPanel/TopBar";
import RegisterMovements from"./AdminRegister/RegisterMovements";
import{useDashboardTheme}from"../../context/DashboardThemeContext";
import{useAlert}from"../../context/AlertContext";
import{useAuth}from"../../context/AuthContext";

function formatMoney(value){
  const number=Number(value||0);
  return number.toLocaleString("es-EC",{minimumFractionDigits:2,maximumFractionDigits:2});
}

function formatDate(value){
  if(!value)return"-";
  try{return new Date(value).toLocaleString("es-EC",{year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"});}
  catch{return"-";}
}

function normalizeDecimalInput(value){
  let normalized=String(value??"").replace(",",".").replace(/[^\d.]/g,"");
  const parts=normalized.split(".");
  if(parts.length>2)normalized=`${parts.shift()}.${parts.join("")}`;
  if(normalized.includes(".")){
    const[integer,decimal]=normalized.split(".");
    normalized=`${integer}.${decimal.slice(0,2)}`;
  }
  return normalized;
}

function errorMessage(data,fallback){
  const value=data?.error||data?.detail;
  if(typeof value==="string")return value;
  if(value&&typeof value==="object")return Object.values(value).flat().map(item=>typeof item==="string"?item:JSON.stringify(item)).join(" ");
  return fallback;
}

export default function AdminRegister(){
  const[sidebarOpen,setSidebarOpen]=useState(true);
  const{dashboardTheme}=useDashboardTheme();
  const{showError,showSuccess}=useAlert();
  const{user}=useAuth();
  const navigate=useNavigate();
  const token=localStorage.getItem("accessToken")||localStorage.getItem("access")||"";
  const permissions=user?.permissions||[];
  const canViewVentas=user?.role==="super_admin"||user?.role==="admin"||permissions.includes("ventas.view");
  const canCreateVentas=user?.role==="super_admin"||user?.role==="admin"||permissions.includes("ventas.create");
  const canEditVentas=user?.role==="super_admin"||user?.role==="admin"||permissions.includes("ventas.edit");
  const[loading,setLoading]=useState(false);
  const[cajaActual,setCajaActual]=useState(null);
  const[movimientos,setMovimientos]=useState([]);
  const[montoInicial,setMontoInicial]=useState("");
  const[fondoAnterior,setFondoAnterior]=useState(null);
  const[observacionApertura,setObservacionApertura]=useState("");
  const[efectivoContado,setEfectivoContado]=useState("");
  const[transferenciaContada,setTransferenciaContada]=useState("");
  const[retiroEfectivo,setRetiroEfectivo]=useState("");
  const[observacionCierre,setObservacionCierre]=useState("");

  const outlinedButtonSx={
    minHeight:40,
    px:2.4,
    borderRadius:"12px",
    textTransform:"none",
    fontWeight:700,
    color:dashboardTheme.button_color,
    borderColor:dashboardTheme.button_color,
    boxShadow:"none",
    "&:hover":{borderColor:dashboardTheme.button_color,bgcolor:`${dashboardTheme.button_color}12`,boxShadow:"none"}
  };

  const cardSx={
    p:{xs:1.5,sm:2,md:2.5},
    borderRadius:"14px",
    border:`1px solid ${dashboardTheme.border_color}`,
    bgcolor:dashboardTheme.card_background,
    boxShadow:"none"
  };

  const infoBoxSx={
    px:1.5,
    py:1.2,
    borderRadius:"10px",
    border:`1px solid ${dashboardTheme.border_color}`,
    bgcolor:dashboardTheme.dashboard_background
  };

  const labelSx={fontSize:13,fontWeight:500,color:dashboardTheme.muted_text_color,lineHeight:1.3};
  const valueSx={fontSize:22,fontWeight:700,color:dashboardTheme.text_color,lineHeight:1.2};
  const estadoCaja=cajaActual?.estado||"SIN_CAJA";
  const efectivoContadoNumero=Number(String(efectivoContado||"0").replace(",","."));
  const retiroEfectivoNumero=Number(String(retiroEfectivo||"0").replace(",","."));
  const fondoRestante=Number.isFinite(efectivoContadoNumero)&&Number.isFinite(retiroEfectivoNumero)?Math.max(efectivoContadoNumero-retiroEfectivoNumero,0):0;

  const resumenCaja=useMemo(()=>[
    {label:"Monto inicial",value:`$${formatMoney(cajaActual?.monto_inicial)}`,icon:<AccountBalanceWalletRoundedIcon sx={{color:dashboardTheme.primary_color}}/>},
    {label:"Efectivo sistema",value:`$${formatMoney(cajaActual?.total_efectivo_sistema)}`,icon:<PointOfSaleRoundedIcon sx={{color:dashboardTheme.primary_color}}/>},
    {label:"Transferencias sistema",value:`$${formatMoney(cajaActual?.total_transferencia_sistema)}`,icon:<CreditCardRoundedIcon sx={{color:dashboardTheme.primary_color}}/>},
    {label:"Efectivo esperado",value:`$${formatMoney(cajaActual?.efectivo_esperado_cierre)}`,icon:<ReceiptLongRoundedIcon sx={{color:dashboardTheme.primary_color}}/>}
  ],[cajaActual,dashboardTheme.primary_color]);

  const cargarFondoAnterior=async()=>{
    if(!canViewVentas)return;
    try{
      const response=await fetch("/api/ventas/caja/listar/?estado=CERRADA&limit=1",{
        headers:{Authorization:`Bearer ${token}`},
        credentials:"include"
      });
      const data=await response.json();
      if(!response.ok)return;
      const ultimaCaja=(data.results||[])[0]||null;
      if(!ultimaCaja){
        setFondoAnterior(null);
        if(montoInicial==="")setMontoInicial("");
        return;
      }
      const fondo=Number(ultimaCaja.fondo_restante_cierre||0);
      const fondoSeguro=Number.isFinite(fondo)&&fondo>=0?fondo:0;
      setFondoAnterior(fondoSeguro);
      setMontoInicial(fondoSeguro.toFixed(2));
    }catch{
      setFondoAnterior(null);
    }
  };

  const loadDetalleCaja=async cajaId=>{
    if(!cajaId)return;
    try{
      const response=await fetch(`/api/ventas/caja/${cajaId}/`,{
        headers:{Authorization:`Bearer ${token}`},
        credentials:"include"
      });
      const data=await response.json();
      if(!response.ok){
        showError(errorMessage(data,"No se pudo cargar el detalle de caja"));
        return;
      }
      setCajaActual(data.caja||null);
      setMovimientos(data.movimientos||[]);
    }catch{
      showError("Error de conexión");
    }
  };

  const loadCajaActual=async()=>{
    if(!canViewVentas)return;
    try{
      setLoading(true);
      const response=await fetch("/api/ventas/caja/actual/",{
        headers:{Authorization:`Bearer ${token}`},
        credentials:"include"
      });
      const data=await response.json();
      if(!response.ok){
        showError(errorMessage(data,"No se pudo consultar la caja"));
        return;
      }
      const caja=data.caja||null;
      setCajaActual(caja);
      if(caja?.id){
        setFondoAnterior(null);
        await loadDetalleCaja(caja.id);
      }else{
        setMovimientos([]);
        await cargarFondoAnterior();
      }
    }catch{
      showError("Error de conexión");
    }finally{
      setLoading(false);
    }
  };

  const abrirCaja=async()=>{
    if(!canCreateVentas){
      showError("No tienes permisos para abrir caja");
      return;
    }
    const monto=Number(String(montoInicial||"").replace(",","."));
    if(montoInicial===""||!Number.isFinite(monto)||monto<0){
      showError("Cuenta el efectivo e ingresa un monto inicial válido");
      return;
    }
    try{
      setLoading(true);
      const response=await fetch("/api/ventas/caja/abrir/",{
        method:"POST",
        headers:{
          "Content-Type":"application/json",
          Authorization:`Bearer ${token}`
        },
        credentials:"include",
        body:JSON.stringify({
          monto_inicial:monto.toFixed(2),
          observacion_apertura:observacionApertura.trim()
        })
      });
      const data=await response.json();
      if(!response.ok){
        showError(errorMessage(data,"No se pudo abrir la caja"));
        return;
      }
      showSuccess(data.mensaje||"Caja abierta correctamente");
      setMontoInicial("");
      setFondoAnterior(null);
      setObservacionApertura("");
      await loadCajaActual();
    }catch{
      showError("Error de conexión");
    }finally{
      setLoading(false);
    }
  };

  const cerrarCaja=async()=>{
    if(!canEditVentas){
      showError("No tienes permisos para cerrar caja");
      return;
    }
    if(!cajaActual?.id){
      showError("No tienes caja abierta");
      return;
    }

    const efectivo=Number(String(efectivoContado||"").replace(",","."));
    const transferencia=Number(String(transferenciaContada||"0").replace(",","."));
    const retiro=Number(String(retiroEfectivo||"0").replace(",","."));

    if(efectivoContado===""||!Number.isFinite(efectivo)||efectivo<0){
      showError("Ingresa el efectivo contado");
      return;
    }
    if(!Number.isFinite(transferencia)||transferencia<0){
      showError("Ingresa un valor válido para transferencias");
      return;
    }
    if(!Number.isFinite(retiro)||retiro<0){
      showError("Ingresa un retiro de efectivo válido");
      return;
    }
    if(retiro>efectivo){
      showError("El retiro no puede superar el efectivo contado");
      return;
    }

    try{
      setLoading(true);
      const response=await fetch(`/api/ventas/caja/${cajaActual.id}/cerrar/`,{
        method:"POST",
        headers:{
          "Content-Type":"application/json",
          Authorization:`Bearer ${token}`
        },
        credentials:"include",
        body:JSON.stringify({
          efectivo_contado_cierre:efectivo.toFixed(2),
          transferencia_contada_cierre:transferencia.toFixed(2),
          retiro_efectivo_cierre:retiro.toFixed(2),
          observacion_cierre:observacionCierre.trim()
        })
      });
      const data=await response.json();
      if(!response.ok){
        showError(errorMessage(data,"No se pudo cerrar la caja"));
        return;
      }
      showSuccess(data.mensaje||"Caja cerrada correctamente");
      const fondo=Number(data?.caja?.fondo_restante_cierre??fondoRestante);
      setCajaActual(null);
      setMovimientos([]);
      setFondoAnterior(Number.isFinite(fondo)?fondo:0);
      setMontoInicial(Number.isFinite(fondo)?fondo.toFixed(2):"0.00");
      setEfectivoContado("");
      setTransferenciaContada("");
      setRetiroEfectivo("");
      setObservacionCierre("");
    }catch{
      showError("Error de conexión");
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    if(!canViewVentas){
      showError("No tienes permisos para acceder a caja");
      navigate("/admin-panel",{replace:true});
      return;
    }
    loadCajaActual();
  },[]);

  return(
    <Box sx={{minHeight:"100vh",bgcolor:dashboardTheme.dashboard_background,display:"flex",color:dashboardTheme.text_color,overflowX:"hidden"}}>
      <Sidebar open={sidebarOpen} onClose={()=>setSidebarOpen(false)}/>
      <Box sx={{flex:1,minWidth:0,minHeight:"100vh",display:"flex",flexDirection:"column"}}>
        <TopBar sidebarOpen={sidebarOpen} onToggleSidebar={()=>setSidebarOpen(prev=>!prev)}/>
        <Box component="main" sx={{flex:1,width:"100%",p:{xs:1.5,sm:2,md:3}}}>
          <Box sx={{width:{xs:"100%",lg:"95%"},mx:"auto",mt:{xs:1,md:1.5}}}>
            <Box sx={{display:"flex",alignItems:{xs:"stretch",sm:"center"},justifyContent:"space-between",flexDirection:{xs:"column",sm:"row"},gap:1.5,mb:{xs:1.5,md:2}}}>
              <Typography sx={{fontSize:{xs:24,sm:26,md:30},fontWeight:600,color:dashboardTheme.primary_color,lineHeight:1.2}}>Caja</Typography>
              <RegisterMovements dashboardTheme={dashboardTheme} cajaAbierta={estadoCaja==="ABIERTA"} canEdit={canEditVentas} onReload={loadCajaActual}/>
            </Box>

            <Paper elevation={0} sx={dashboardTheme.tableStyles.wrapper}>
              <Box sx={dashboardTheme.tableStyles.toolbar}>
                <Stack direction={{xs:"column",md:"row"}} spacing={1.5} alignItems={{xs:"stretch",md:"center"}} justifyContent="space-between">
                  <Box>
                    <Typography sx={{fontSize:16,fontWeight:700,color:dashboardTheme.text_color}}>
                      Estado: {estadoCaja==="ABIERTA"?"Caja abierta":"Sin caja abierta"}
                    </Typography>
                    <Typography sx={{fontSize:13,fontWeight:400,color:dashboardTheme.muted_text_color,mt:.3}}>
                      {cajaActual?.fecha_apertura?`Apertura: ${formatDate(cajaActual.fecha_apertura)}`:"Cuenta el efectivo disponible y confirma el monto de apertura"}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Box sx={{p:{xs:1.5,sm:2,md:2.5}}}>
                {!cajaActual&&(
                  <Box sx={cardSx}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{mb:2}}>
                      <LockOpenRoundedIcon sx={{color:dashboardTheme.primary_color}}/>
                      <Typography sx={{fontSize:18,fontWeight:700,color:dashboardTheme.text_color}}>Abrir caja</Typography>
                    </Stack>

                    {fondoAnterior!==null&&(
                      <Box sx={{...infoBoxSx,mb:1.5}}>
                        <Typography sx={{fontSize:12.5,fontWeight:500,color:dashboardTheme.muted_text_color}}>Fondo dejado en el último cierre</Typography>
                        <Typography sx={{fontSize:20,fontWeight:700,color:dashboardTheme.text_color,mt:.3}}>${formatMoney(fondoAnterior)}</Typography>
                        <Typography sx={{fontSize:12.5,fontWeight:400,color:dashboardTheme.muted_text_color,mt:.5}}>
                          El sistema lo carga como sugerencia. Cuenta físicamente el dinero y corrige el monto antes de abrir si existe alguna diferencia.
                        </Typography>
                      </Box>
                    )}

                    <Stack direction={{xs:"column",md:"row"}} spacing={1.5}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Efectivo contado para apertura"
                        value={montoInicial}
                        onChange={e=>setMontoInicial(normalizeDecimalInput(e.target.value))}
                        inputMode="decimal"
                        sx={dashboardTheme.inputStyles}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Observación"
                        value={observacionApertura}
                        onChange={e=>setObservacionApertura(e.target.value)}
                        sx={dashboardTheme.inputStyles}
                      />
                      <Button variant="contained" startIcon={<LockOpenRoundedIcon/>} onClick={abrirCaja} disabled={loading} sx={dashboardTheme.buttonStyles.contained}>
                        {loading?"Abriendo...":"Abrir caja"}
                      </Button>
                    </Stack>
                  </Box>
                )}

                {cajaActual&&(
                  <>
                    <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"repeat(2,1fr)",md:"repeat(4,1fr)"},gap:1.5,mb:2}}>
                      {resumenCaja.map(item=>(
                        <Box key={item.label} sx={cardSx}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{mb:.8}}>
                            {item.icon}
                            <Typography sx={labelSx}>{item.label}</Typography>
                          </Stack>
                          <Typography sx={valueSx}>{item.value}</Typography>
                        </Box>
                      ))}
                    </Box>

                    <Box sx={cardSx}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{mb:2}}>
                        <LockRoundedIcon sx={{color:dashboardTheme.primary_color}}/>
                        <Typography sx={{fontSize:18,fontWeight:700,color:dashboardTheme.text_color}}>Cerrar caja</Typography>
                      </Stack>

                      <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"repeat(2,1fr)",lg:"repeat(4,1fr)"},gap:1.5}}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Efectivo contado"
                          value={efectivoContado}
                          onChange={e=>setEfectivoContado(normalizeDecimalInput(e.target.value))}
                          inputMode="decimal"
                          sx={dashboardTheme.inputStyles}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Transferencia contada"
                          value={transferenciaContada}
                          onChange={e=>setTransferenciaContada(normalizeDecimalInput(e.target.value))}
                          inputMode="decimal"
                          sx={dashboardTheme.inputStyles}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Retiro a efectivo general"
                          value={retiroEfectivo}
                          onChange={e=>setRetiroEfectivo(normalizeDecimalInput(e.target.value))}
                          inputMode="decimal"
                          helperText="Dinero que sale físicamente de la caja."
                          sx={dashboardTheme.inputStyles}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Observación cierre"
                          value={observacionCierre}
                          onChange={e=>setObservacionCierre(e.target.value)}
                          sx={dashboardTheme.inputStyles}
                        />
                      </Box>

                      <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"repeat(3,1fr)"},gap:1.2,mt:1.5}}>
                        <Box sx={infoBoxSx}>
                          <Typography sx={labelSx}>Efectivo contado</Typography>
                          <Typography sx={{...valueSx,fontSize:19}}>${formatMoney(efectivoContadoNumero)}</Typography>
                        </Box>
                        <Box sx={infoBoxSx}>
                          <Typography sx={labelSx}>Pasa a efectivo general</Typography>
                          <Typography sx={{...valueSx,fontSize:19}}>${formatMoney(retiroEfectivoNumero)}</Typography>
                        </Box>
                        <Box sx={infoBoxSx}>
                          <Typography sx={labelSx}>Fondo que queda en caja</Typography>
                          <Typography sx={{...valueSx,fontSize:19}}>${formatMoney(fondoRestante)}</Typography>
                        </Box>
                      </Box>

                      <Box sx={{display:"flex",justifyContent:"flex-end",mt:1.5}}>
                        <Button variant="outlined" onClick={cerrarCaja} disabled={loading} sx={outlinedButtonSx}>
                          {loading?"Cerrando...":"Cerrar caja"}
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>

              <Box sx={dashboardTheme.tableStyles.tableArea}>
                <TableContainer sx={dashboardTheme.tableStyles.container}>
                  <Table size="small" sx={dashboardTheme.tableStyles.table}>
                    <TableHead>
                      <TableRow sx={dashboardTheme.tableStyles.headRow}>
                        <TableCell sx={dashboardTheme.tableStyles.headCell}>Fecha</TableCell>
                        <TableCell sx={dashboardTheme.tableStyles.headCell}>Origen</TableCell>
                        <TableCell sx={dashboardTheme.tableStyles.headCell}>Tipo</TableCell>
                        <TableCell sx={dashboardTheme.tableStyles.headCell}>Forma pago</TableCell>
                        <TableCell sx={dashboardTheme.tableStyles.headCell}>Concepto</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Monto</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {movimientos.map(movimiento=>(
                        <TableRow key={movimiento.id} hover sx={dashboardTheme.tableStyles.bodyRow}>
                          <TableCell sx={{whiteSpace:"nowrap"}}>{formatDate(movimiento.creado_en)}</TableCell>
                          <TableCell sx={{whiteSpace:"nowrap"}}>
                            {movimiento.origen==="VENTA"?"Venta":movimiento.origen==="COMPRA"?"Compra":movimiento.origen==="MANUAL"?"Manual":"Sistema"}
                          </TableCell>
                          <TableCell sx={{whiteSpace:"nowrap"}}>{movimiento.tipo==="INGRESO"?"Ingreso":"Egreso"}</TableCell>
                          <TableCell sx={{whiteSpace:"nowrap"}}>{movimiento.forma_pago==="TRANSFERENCIA"?"Transferencia":"Efectivo"}</TableCell>
                          <TableCell>
                            <Box>
                              <Typography sx={{fontSize:14,fontWeight:600,color:dashboardTheme.text_color,lineHeight:1.25}}>
                                {movimiento.concepto||"-"}
                              </Typography>
                              {(movimiento.referencia_visible&&movimiento.referencia_visible!=="-"||movimiento.observacion)&&(
                                <Typography sx={{fontSize:12.5,fontWeight:400,color:dashboardTheme.muted_text_color,mt:.3}}>
                                  {movimiento.referencia_visible&&movimiento.referencia_visible!=="-"?movimiento.referencia_visible:movimiento.observacion}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{whiteSpace:"nowrap",fontWeight:700}}>
                            {movimiento.tipo==="EGRESO"?"-":"+"}${formatMoney(movimiento.monto)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {!loading&&movimientos.length===0&&(
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={dashboardTheme.tableStyles.emptyCell}>No hay movimientos registrados</TableCell>
                        </TableRow>
                      )}
                      {loading&&(
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={dashboardTheme.tableStyles.emptyCell}>Cargando caja...</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}